// Project workspace: Publish, Versions, Players, Settings - ported from the
// design source (screens-project-ops.jsx) and bound to the live publish,
// analytics and project services.
import { Fragment, useEffect, useMemo, useState } from 'react';
import { toast } from '../design/toast';

import { diffInstances, type MapDocument } from '../../engine';
import { initialsOf, timeAgo } from '../app/screens-app';
import { EmptyState } from '../components/EmptyState';
import { Link, navigate } from '../design/router';
import {
  createSceneSnapshot,
  deleteProject,
  listSceneSnapshots,
  loadSceneSnapshot,
  renameProject,
  saveMapDocument,
  type SceneSnapshotMeta,
} from '../persistence/projectRepository';
import {
  publishProject,
  restorePublicationVersion,
  unpublish,
  updatePublication,
  type Visibility,
} from '../services/publish';
import {
  analyticsGames,
  gameMatches,
  getProfile,
  type GameOverview,
  type MatchSummary,
} from '../services/settings';
import { MapAuditPanel } from '../editor/MapAuditPanel';
import { EdConfirmDialog, EdTypedConfirmDialog } from '../editor/editor-panels';
import { CopyField, LiveBadge, PjIcon, PJ_ICONS, PjShell, PjStat, useGameSetupSummary, useWorkspace } from './screens-project';

const VISIBILITY_UI: { id: string; wire: Visibility; hint: string }[] = [
  { id: 'Public', wire: 'public', hint: 'Listed in the showcase, anyone with the link can play.' },
  { id: 'Unlisted', wire: 'unlisted', hint: 'Playable by link only, hidden from the showcase.' },
  { id: 'Off', wire: 'private', hint: "Servers stopped, the play URL shows as unavailable (players can't tell it apart from a link that never existed)." },
];

/** The project's export targets and their status, shown on the Settings
 * screen. The PWA row went live with SF18.2 (per-slug manifest, minimal
 * service worker); desktop wrap remains a future export target. */
export const EXPORT_TARGETS: [name: string, status: string, live: boolean][] = [
  ['Web (play URL)', 'Live', true],
  ['Installable app (PWA)', 'Live', true],
  ['Desktop wrap', 'Coming soon', false],
];

/* ── Publish ──────────────────────────────────────────────────────────── */
export function ScreenProjectPublish() {
  const { projectId, project, publication, refresh } = useWorkspace();
  const { multiplayer } = useGameSetupSummary();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const live = publication !== null && publication.visibility !== 'private';
  const nextVersion = (publication?.version ?? 0) + 1;
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  async function publish() {
    setBusy(true);
    setError(null);
    try {
      await publishProject(projectId, { note: note.trim() || undefined });
      setNote('');
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function setVisibility(wire: Visibility) {
    if (!publication || publication.visibility === wire) return;
    setError(null);
    try {
      await updatePublication(projectId, { visibility: wire });
      refresh();
    } catch (err) {
      // The Visibility card is well below the one inline error slot this
      // page has (next to Publish), so a failure here would otherwise show
      // no feedback anywhere near the button the creator actually clicked.
      const msg = err instanceof Error ? err.message : 'Could not change visibility.';
      setError(msg);
      toast(msg, 'error');
    }
  }

  async function deletePublication() {
    if (!publication) return;
    setConfirmingDelete(false);
    setError(null);
    try {
      await unpublish(projectId);
      refresh();
    } catch (err) {
      // Same reasoning as setVisibility: this runs after the confirm dialog
      // has already closed, well below the inline error slot.
      const msg = err instanceof Error ? err.message : 'Could not delete the publication.';
      setError(msg);
      toast(msg, 'error');
    }
  }

  return (
    <PjShell tab="publish">
      <div className="pj-page">
        <div className="pj-page-head">
          <div>
            <h1>Publish & hosting</h1>
            <p>One snapshot, one URL, servers managed for you</p>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {publication ? (
            <div className="set-card" style={{ borderColor: live ? 'color-mix(in srgb, var(--ss-success) 20%, transparent)' : undefined }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      background: live ? 'var(--ss-success)' : 'var(--ss-text-3)',
                      boxShadow: live ? '0 0 12px color-mix(in srgb, var(--ss-success) 50%, transparent)' : undefined,
                    }}
                  />
                  <div>
                    <h3>
                      {live ? 'Live' : 'Off'} · v{publication.version}
                    </h3>
                    <span className="hint">
                      {publication.updated_at ? `Published ${timeAgo(new Date(publication.updated_at).getTime())}` : 'Published'}
                    </span>
                  </div>
                </div>
                {live && (
                  <a className="ss-btn ss-btn--ghost ss-btn--sm" href={`https://${publication.url.replace(/^https?:\/\//, '')}`} target="_blank" rel="noreferrer">
                    View live game
                  </a>
                )}
              </div>
              <CopyField value={publication.url.replace(/^https?:\/\//, '')} />
            </div>
          ) : (
            <div className="set-card">
              <h3>Not published yet</h3>
              <span className="hint">Publishing snapshots the current project and puts it live at its own URL.</span>
            </div>
          )}

          <div className="set-card">
            <h3>Pre-flight map audit</h3>
            <span className="hint">Automated readiness checks before publishing your map.</span>
            <MapAuditPanel map={project?.map} />
          </div>

          <div className="set-card">
            <h3>Publish a new version</h3>
            <span className="hint">Snapshots the current project state. Players get the new version the next time they play.</span>
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                className="ss-input"
                placeholder="What changed? e.g. New mid cover, fixed spawn camp angle"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <button className="ss-btn ss-btn--primary" style={{ flexShrink: 0 }} onClick={publish} disabled={busy}>
                {busy ? 'Publishing…' : `Publish v${nextVersion}`}
              </button>
            </div>
            {error && (
              <p style={{ color: 'var(--ss-danger)', fontSize: 13 }} role="alert">
                {error}
              </p>
            )}
          </div>

          <div className="set-card">
            <h3>Visibility</h3>
            <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {VISIBILITY_UI.map((o) => {
                const selected = publication ? publication.visibility === o.wire : false;
                return (
                  <button
                    key={o.id}
                    className="ss-card"
                    onClick={() => void setVisibility(o.wire)}
                    disabled={!publication}
                    style={{
                      textAlign: 'left',
                      padding: '14px 16px',
                      borderRadius: 12,
                      border: '1px solid ' + (selected ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
                      background: selected ? 'var(--ss-accent-soft)' : 'transparent',
                      color: 'var(--ss-text)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 5,
                      opacity: publication ? 1 : 0.55,
                      cursor: publication ? 'pointer' : 'default',
                    }}
                  >
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: selected ? 'var(--ss-accent)' : 'var(--ss-text)' }}>{o.id}</span>
                    <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)', lineHeight: 1.45 }}>{o.hint}</span>
                  </button>
                );
              })}
            </div>
            {!publication && <span className="hint">Visibility unlocks after the first publish.</span>}
          </div>

          {publication && (
            <div className="set-card" style={{ borderColor: 'color-mix(in srgb, var(--ss-danger) 25%, transparent)' }}>
              <h3>Delete publication</h3>
              <span className="hint">
                Releases the play URL back into the pool and clears version history. Setting visibility to Off stops the game without giving up the URL - this is for when you want the slug back.
              </span>
              <button className="ss-btn ss-btn--danger ss-btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => setConfirmingDelete(true)}>
                Delete publication
              </button>
            </div>
          )}

          {multiplayer && (
            <div className="set-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3>Player pool</h3>
                <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 11, color: 'var(--ss-text-3)' }}>50 concurrent included</span>
              </div>
              <div style={{ height: 5, borderRadius: 999, background: 'var(--ss-border)' }}>
                <div style={{ width: '0%', height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, var(--ss-primary-2), var(--ss-accent))' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="hint">Live pool usage appears here while your game is online. Extra players queue rather than degrade matches.</span>
                <Link to="/pricing" className="ss-btn ss-btn--ghost ss-btn--sm" style={{ flexShrink: 0 }}>
                  Add capacity
                </Link>
              </div>
            </div>
          )}

          <div className="set-card" style={{ opacity: 0.75 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3>Custom domain</h3>
              <span
                style={{
                  fontFamily: 'var(--ss-sans)',
                  fontWeight: 500,
                  fontSize: 9.5,
                  letterSpacing: '0.14em',
                  color: 'var(--ss-accent)',
                  border: '1px solid var(--ss-accent-border)',
                  borderRadius: 999,
                  padding: '3px 9px',
                }}
              >
                PRO
              </span>
            </div>
            <span className="hint">Serve your game from play.yourstudio.com. Available on the Pro plan.</span>
          </div>
        </div>
      </div>
      {confirmingDelete && publication && (
        <EdTypedConfirmDialog
          title="Delete this publication?"
          body={`The play URL (${publication.url.replace(/^https?:\/\//, '')}) is released back into the pool and version history is cleared - this can't be undone.`}
          confirmLabel="Delete publication"
          requiredText="DELETE"
          onConfirm={() => void deletePublication()}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}
    </PjShell>
  );
}

/* ── Versions ─────────────────────────────────────────────────────────── */
export function ScreenProjectVersions() {
  const { projectId, versions, refresh } = useWorkspace();
  const [busyVersion, setBusyVersion] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingVersion, setConfirmingVersion] = useState<number | null>(null);

  async function restore(version: number) {
    setConfirmingVersion(null);
    setBusyVersion(version);
    setError(null);
    try {
      await restorePublicationVersion(projectId, version);
      refresh();
    } catch (err) {
      // The inline error sits at the top of a list that can run well past
      // one screen, off-screen from whichever row's Restore was clicked.
      const msg = err instanceof Error ? err.message : 'Restore failed.';
      setError(msg);
      toast(msg, 'error');
    } finally {
      setBusyVersion(null);
    }
  }

  return (
    <PjShell tab="versions">
      <div className="pj-page">
        <div className="pj-page-head">
          <div>
            <h1>Versions</h1>
            <p>Every publish is a restorable snapshot</p>
          </div>
        </div>
        {error && (
          <p style={{ color: 'var(--ss-danger)', fontSize: 13, marginBottom: 10 }} role="alert">
            {error}
          </p>
        )}
        {versions.length === 0 ? (
          <EmptyState
            icon={<PjIcon d={PJ_ICONS.versions!} size={19} />}
            title="No versions yet"
            hint="Every publish becomes a restorable snapshot here, with the live one marked."
          />
        ) : (
          <div style={{ position: 'relative' }}>
            <div className="vtl-rail" />
            {versions.map((r) => (
              <div key={r.version} className="ss-row" style={{ padding: '14px 4px', borderTop: 'none' }}>
                <span className={'vtl-dot' + (r.live ? ' is-live' : '')} />
                <span
                  style={{
                    fontFamily: 'var(--ss-sans)',
                    fontSize: 13,
                    fontWeight: 600,
                    color: r.live ? 'var(--ss-accent)' : 'var(--ss-text-2)',
                    width: 40,
                  }}
                >
                  v{r.version}
                </span>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ color: 'var(--ss-text)', fontWeight: 500 }}>{r.note || 'Published'}</span>
                  <span style={{ fontSize: 12, color: 'var(--ss-text-3)' }}>
                    {r.created_at ? timeAgo(new Date(r.created_at).getTime()) : ''}
                  </span>
                </div>
                {r.live && <LiveBadge />}
                {!r.live && (
                  <button
                    className="ss-btn ss-btn--ghost row-action"
                    style={{ padding: '5px 13px', fontSize: 12, borderRadius: 8 }}
                    onClick={() => setConfirmingVersion(r.version)}
                    disabled={busyVersion !== null}
                  >
                    {busyVersion === r.version ? 'Restoring…' : 'Restore'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {versions.length > 0 && (
          <p className="ss-hint" style={{ marginTop: 24 }}>
            Restoring copies a snapshot into the editor. Nothing goes live until you publish it.
          </p>
        )}
        <SceneHistory />
      </div>
      {confirmingVersion !== null && (
        <EdConfirmDialog
          title={`Restore v${confirmingVersion}?`}
          body="This copies the version into the editor. Nothing goes live until you publish it."
          confirmLabel="Restore"
          onConfirm={() => void restore(confirmingVersion)}
          onCancel={() => setConfirmingVersion(null)}
        />
      )}
    </PjShell>
  );
}

/* ── Scene history: editing snapshots beyond the publish timeline ─────── */
interface SnapshotDiffView {
  added: string[];
  removed: string[];
  modified: string[];
}

function snapshotDiff(snapshot: MapDocument, current: MapDocument): SnapshotDiffView {
  const label = (i: { id: string; name?: string }) => i.name ?? i.id;
  const diff = diffInstances(snapshot.objectInstances ?? [], current.objectInstances ?? []);
  return {
    added: diff.added.map(label),
    removed: diff.removed.map(label),
    modified: diff.modified.map((m) => label(m.after)),
  };
}

function SceneHistory() {
  const { projectId, project, refresh } = useWorkspace();
  const [snapshots, setSnapshots] = useState<SceneSnapshotMeta[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [diff, setDiff] = useState<SnapshotDiffView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingRow, setConfirmingRow] = useState<SceneSnapshotMeta | null>(null);

  useEffect(() => {
    let cancelled = false;
    listSceneSnapshots(projectId)
      .then((rows) => {
        if (!cancelled) setSnapshots(rows.slice(0, 20));
      })
      .catch((err: unknown) => {
        if (!cancelled) console.error('[ScreenProjectHistory] failed to load snapshots:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  async function compare(id: string) {
    if (open === id) {
      setOpen(null);
      setDiff(null);
      return;
    }
    setError(null);
    setOpen(id);
    setDiff(null);
    try {
      const snap = await loadSceneSnapshot(projectId, id);
      setDiff(project ? snapshotDiff(snap, project.map) : null);
    } catch {
      const msg = 'Could not load that snapshot.';
      setError(msg);
      toast(msg, 'error');
      setOpen(null);
    }
  }

  async function restore(row: SceneSnapshotMeta) {
    if (!project) return;
    setConfirmingRow(null);
    setBusy(row.id);
    setError(null);
    try {
      // Safety net before the safety net: keep the scene being replaced.
      await createSceneSnapshot(projectId, 'Before restore', project.map);
      const snap = await loadSceneSnapshot(projectId, row.id);
      await saveMapDocument(projectId, snap);
      refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Restore failed.';
      setError(msg);
      toast(msg, 'error');
    } finally {
      setBusy(null);
    }
  }

  const diffLine = (label: string, names: string[], colour: string) =>
    names.length > 0 && (
      <div style={{ fontSize: 12, color: 'var(--ss-text-2)' }}>
        <span style={{ color: colour, fontWeight: 600 }}>{label} {names.length}</span>
        <span style={{ color: 'var(--ss-text-3)' }}> · {names.slice(0, 6).join(', ')}{names.length > 6 ? '…' : ''}</span>
      </div>
    );

  return (
    <div style={{ marginTop: 36 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Scene history</h2>
      <p className="ss-hint" style={{ marginBottom: 12 }}>
        Editing snapshots, taken automatically while you work and before every publish and restore.
      </p>
      {error && <p style={{ color: 'var(--ss-danger)', fontSize: 12.5, marginBottom: 8 }} role="alert">{error}</p>}
      {snapshots.length === 0 ? (
        <p className="ss-hint">No scene snapshots yet. They appear as you edit.</p>
      ) : (
        snapshots.map((row) => (
          <div key={row.id} style={{ borderTop: '1px solid var(--ss-border-soft)' }}>
            <div className="ss-row" style={{ padding: '12px 4px', borderTop: 'none' }}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ color: 'var(--ss-text)', fontWeight: 500, fontSize: 13.5 }}>{row.name || 'Snapshot'}</span>
                <span style={{ fontSize: 12, color: 'var(--ss-text-3)' }}>{timeAgo(new Date(row.created_at).getTime())}</span>
              </div>
              <button className="ss-btn ss-btn--ghost" style={{ padding: '5px 13px', fontSize: 12, borderRadius: 8 }}
                onClick={() => void compare(row.id)}>{open === row.id ? 'Hide diff' : 'Compare'}</button>
              <button className="ss-btn ss-btn--ghost" style={{ padding: '5px 13px', fontSize: 12, borderRadius: 8 }}
                onClick={() => setConfirmingRow(row)} disabled={busy !== null}>
                {busy === row.id ? 'Restoring…' : 'Restore'}
              </button>
            </div>
            {open === row.id && (
              <div style={{ padding: '0 4px 14px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                {diff === null ? (
                  <span style={{ fontSize: 12, color: 'var(--ss-text-3)' }}>Comparing…</span>
                ) : diff.added.length + diff.removed.length + diff.modified.length === 0 ? (
                  <span style={{ fontSize: 12, color: 'var(--ss-text-3)' }}>The scene's objects match this snapshot.</span>
                ) : (
                  <Fragment>
                    {diffLine('Added since', diff.added, 'var(--ss-success)')}
                    {diffLine('Removed since', diff.removed, 'var(--ss-danger)')}
                    {diffLine('Changed since', diff.modified, 'var(--ss-accent)')}
                  </Fragment>
                )}
              </div>
            )}
          </div>
        ))
      )}
      {confirmingRow && (
        <EdConfirmDialog
          title={`Restore "${confirmingRow.name || 'snapshot'}"?`}
          body="The current scene is snapshotted first, so nothing is lost."
          confirmLabel="Restore"
          onConfirm={() => void restore(confirmingRow)}
          onCancel={() => setConfirmingRow(null)}
        />
      )}
    </div>
  );
}

/* ── Players & analytics ──────────────────────────────────────────────── */
function matchDuration(m: MatchSummary): string {
  if (!m.started_at || !m.ended_at) return '';
  const mins = Math.max(1, Math.round((new Date(m.ended_at).getTime() - new Date(m.started_at).getTime()) / 60000));
  return `${mins}m`;
}

export function ScreenProjectPlayers() {
  const { projectId, publication, project } = useWorkspace();
  const { teams, multiplayer, competitive } = useGameSetupSummary();
  const [game, setGame] = useState<GameOverview | null>(null);
  const [matches, setMatches] = useState<MatchSummary[]>([]);
  const [sessions7d, setSessions7d] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // A Solo project has no player pool or match history to fetch - by the
    // time this runs, `multiplayer` already reflects the real, WorkspaceProvider-
    // loaded Game Setup doc (this whole tab only mounts once that load has
    // settled - see PjShell), not a fallback that could still flip.
    if (!multiplayer) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const games = await analyticsGames();
        const slug = publication?.slug;
        const name = project?.meta.name.toLowerCase();
        const match = games.find((g) => g.slug === slug || g.name.toLowerCase() === name) ?? null;
        if (cancelled) return;
        setGame(match);
        if (match) {
          const ms = await gameMatches(match.id);
          if (!cancelled) {
            setMatches(ms);
            const cutoff = Date.now() - 7 * 86400000;
            setSessions7d(ms.filter((m) => m.started_at && new Date(m.started_at).getTime() >= cutoff).length);
          }
        }
      } catch {
        // analytics absent is a valid state pre-launch
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [publication, project, multiplayer]);

  const avgSession = useMemo(() => {
    const durations = matches
      .filter((m) => m.started_at && m.ended_at)
      .map((m) => new Date(m.ended_at!).getTime() - new Date(m.started_at!).getTime());
    if (durations.length === 0) return '0m';
    const avgMs = durations.reduce((a, b) => a + b, 0) / durations.length;
    const mins = Math.floor(avgMs / 60000);
    const secs = Math.round((avgMs % 60000) / 1000);
    return `${mins}m ${secs}s`;
  }, [matches]);

  return (
    <PjShell tab="players">
      <div className="pj-page">
        <div className="pj-page-head">
          <div>
            <h1>Players & analytics</h1>
            <p>{multiplayer ? 'Live pool, sessions and match results' : 'For projects with more than one player at a time'}</p>
          </div>
        </div>
        {!multiplayer ? (
          <EmptyState
            icon={<PjIcon d={PJ_ICONS.players!} size={22} />}
            title="This project is Solo"
            hint="Player pool and match analytics apply once a project has more than one player at a time. Switch Game type to Co-op or Versus in Game Setup to enable them."
            action={
              <Link to={`/app/editor/${projectId}/game`} className="ss-btn ss-btn--ghost ss-btn--sm">
                Open Game Setup
              </Link>
            }
          />
        ) : (
          <>
            <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
              <PjStat n={String(game?.player_count ?? 0)} l="Players in pool" />
              <PjStat n={String(game?.match_count ?? 0)} l="Matches, all time" />
              <PjStat n={String(sessions7d)} l="Matches, 7 days" />
              <PjStat n={avgSession} l="Avg match" dim />
            </div>
            <div className="set-card" style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3>Concurrent players</h3>
              </div>
              <p className="hint" style={{ padding: '18px 0' }}>
                Concurrent player history appears here once your game is live on the realtime tier.
              </p>
            </div>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Recent matches</h3>
            {!loaded && <p style={{ fontSize: 13, color: 'var(--ss-text-3)', padding: '12px 0' }}>Loading…</p>}
            {loaded && matches.length === 0 && (
              <EmptyState
                compact
                icon={<PjIcon d={PJ_ICONS.players!} size={19} />}
                title="No matches recorded yet"
                hint="Results land here as games finish."
              />
            )}
            {matches.slice(0, 8).map((m) => {
              const when = m.started_at
                ? new Date(m.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                : '';
              // raiders/wardens are Sunder's own faction names - the score
              // pair only means something for a genuinely Versus project, so
              // it's gated on `competitive` alone (unlike an earlier version
              // of this gate, `competitive` no longer needs a second,
              // per-match corroborating signal: it's read from
              // WorkspaceProvider's own load, which every tab already
              // blocks on via PjShell's loading gate - see useGameSetupSummary's
              // doc comment - so it can't still be showing GAMESETUP_DOC_FALLBACK's
              // 'Versus' default while the real doc is in flight). Within a
              // competitive match, a winner is shown with its name and
              // colour-coded by faction; a drawn/incomplete match (winner
              // null) still shows its bare score, same as before this genre
              // gate existed.
              const modeLabel = competitive ? `${teams} match` : 'Match';
              const result = competitive
                ? m.winner
                  ? `${m.winner.charAt(0).toUpperCase()}${m.winner.slice(1)} ${m.raiders_score}-${m.wardens_score}`
                  : `${m.raiders_score}-${m.wardens_score}`
                : null;
              return (
                <div key={m.id} className="ss-row">
                  <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 12, color: 'var(--ss-text-3)', width: 44 }}>{when}</span>
                  <span style={{ flex: 1, color: 'var(--ss-text-2)' }}>{modeLabel}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--ss-text-3)', width: 80 }}>{m.map_id ?? ''}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, width: 110, color: m.winner === 'raiders' ? 'var(--ss-accent)' : 'var(--ss-info)' }}>
                    {result}
                  </span>
                  <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 11.5, color: 'var(--ss-text-3)', width: 36, textAlign: 'right' }}>
                    {matchDuration(m)}
                  </span>
                </div>
              );
            })}
          </>
        )}
      </div>
    </PjShell>
  );
}

/* ── Settings ─────────────────────────────────────────────────────────── */
export function ScreenProjectSettings() {
  const { projectId, project, publication, refresh } = useWorkspace();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [owner, setOwner] = useState<{ name: string; email: string } | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteHint, setInviteHint] = useState(false);
  const [transferHint, setTransferHint] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  useEffect(() => {
    setName(project?.meta.name ?? '');
    setSlug(publication?.slug ?? '');
  }, [project, publication]);

  useEffect(() => {
    let cancelled = false;
    getProfile()
      .then((p) => {
        if (!cancelled) setOwner({ name: p.display_name || p.email, email: p.email });
      })
      .catch((err: unknown) => {
        if (!cancelled) console.error('[ScreenProjectSettings] failed to load profile:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function save() {
    setError(null);
    setSaved(false);
    try {
      if (project && name.trim() && name.trim() !== project.meta.name) {
        await renameProject(projectId, name.trim());
      }
      if (publication && slug.trim() && slug.trim() !== publication.slug) {
        await updatePublication(projectId, { slug: slug.trim() });
      }
      refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save changes.';
      setError(msg);
      toast(msg, 'error');
    }
  }

  async function removeProject() {
    setConfirmingRemove(false);
    try {
      await deleteProject(projectId);
      navigate('/app');
    } catch (err) {
      // This page's one inline error slot lives up in the General card, far
      // from the danger-zone button that was actually clicked (and the
      // confirm dialog has already closed by this point) - a failed delete
      // needs its own visible signal, not one an owner has to scroll for.
      const msg = err instanceof Error ? err.message : 'Delete failed.';
      setError(msg);
      toast(msg, 'error');
    }
  }

  return (
    <PjShell tab="settings">
      <div className="pj-page">
        <div className="pj-page-head">
          <div>
            <h1>Project settings</h1>
            <p>Name, team and the dangerous stuff</p>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="set-card">
            <h3>General</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="ss-field">
                <span className="ss-field-label">Project name</span>
                <input className="ss-input" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="ss-field">
                <span className="ss-field-label">Play URL slug</span>
                <input
                  className="ss-input"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder={publication ? '' : 'Set on first publish'}
                  disabled={!publication}
                  style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 13 }}
                />
              </div>
            </div>
            {error && (
              <p style={{ color: 'var(--ss-danger)', fontSize: 13 }} role="alert">
                {error}
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="ss-btn ss-btn--primary ss-btn--sm" onClick={save}>
                {saved ? 'Saved' : 'Save changes'}
              </button>
            </div>
          </div>

          <div className="set-card">
            <h3>Collaborators</h3>
            {owner && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="app-avatar" style={{ width: 30, height: 30, fontSize: 11 }}>
                  {initialsOf(owner.name)}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 500 }}>{owner.name}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)' }}>{owner.email}</span>
                </div>
                <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)' }}>Owner</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <input className="ss-input" placeholder="teammate@studio.dev" />
              <button className="ss-btn ss-btn--ghost ss-btn--sm" style={{ flexShrink: 0 }} onClick={() => setInviteHint(true)}>
                Invite
              </button>
            </div>
            {inviteHint && <span className="hint">Invites arrive with team seats. Your tenant is single-seat for now.</span>}
          </div>

          <div className="set-card">
            <h3>Export targets</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {EXPORT_TARGETS.map(([t, s, liveTarget]) => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13.5 }}>
                  <span>{t}</span>
                  <span style={{ fontSize: 11.5, color: liveTarget ? 'var(--ss-success)' : 'var(--ss-text-3)' }}>{s}</span>
                </div>
              ))}
            </div>
            <hr className="ss-hairline" />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <span className="hint">Download a .sstk backup: the map, every editor document and settings. Uploaded files are not included.</span>
              <button
                className="ss-btn ss-btn--ghost ss-btn--sm"
                style={{ flexShrink: 0 }}
                disabled={exporting}
                onClick={() => {
                  setExporting(true);
                  void import('../persistence/sstkArchive')
                    .then(({ exportProjectArchive, downloadArchive }) =>
                      exportProjectArchive(projectId).then(downloadArchive),
                    )
                    .finally(() => setExporting(false));
                }}
              >
                {exporting ? 'Exporting…' : 'Export project (.sstk)'}
              </button>
            </div>
          </div>

          <div className="set-card" style={{ borderColor: 'color-mix(in srgb, var(--ss-danger) 25%, transparent)' }}>
            <h3 style={{ color: 'var(--ss-danger)' }}>Danger zone</h3>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <span className="hint">Transfer this project to another tenant, including its live URL and versions.</span>
              <button className="ss-btn ss-btn--ghost ss-btn--sm" style={{ flexShrink: 0 }} onClick={() => setTransferHint(true)}>
                Transfer
              </button>
            </div>
            {transferHint && <span className="hint">Transfers arrive with team seats.</span>}
            <hr className="ss-hairline" />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <span className="hint">Delete the project, its versions and assets. The play URL is released. This cannot be undone.</span>
              <button
                className="ss-btn ss-btn--danger ss-btn--sm"
                style={{ flexShrink: 0 }}
                onClick={() => setConfirmingRemove(true)}
              >
                Delete project
              </button>
            </div>
          </div>
        </div>
      </div>
      {confirmingRemove && (
        <EdTypedConfirmDialog
          title="Delete this project?"
          body="Deletes the project, its versions and assets. The play URL is released. This can't be undone."
          confirmLabel="Delete project"
          requiredText={project?.meta.name ?? ''}
          onConfirm={() => void removeProject()}
          onCancel={() => setConfirmingRemove(false)}
        />
      )}
    </PjShell>
  );
}

// Re-export for callers that treated publish/unpublish as page-level actions.
export { unpublish };
